import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  LinearProgress,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  EmojiEvents as TrophyIcon,
  Group as GroupIcon,
  Quiz as QuizIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { resolveCurriculumChain } from '../hooks/useDeliverablesPolling.ts';
import type { StudentDeliverable } from '../types/curriculum.types.ts';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useRecoilValue } from 'recoil';
import { curriculumSelectionAtom } from './Curriculumselectionatom.ts';
import BoardClassSubjectSelector from './Boardclasssubjectselector.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Subtopic {
  subtopic: string;
  marks: number;
  prerequisites?: { name: string }[];
}

interface Topic {
  topic: string;
  description?: string;
  subtopics: Subtopic[];
  learning_material?: string[];
  topic_prerequisites?: { name: string }[];
}

interface KnowledgeMap {
  id?: string;
  _id?: string;
  board: string;
  class: string;
  subject: string;
  topics: Topic[];
  books?: string[];
}

interface ProblemStep {
  step: number;
  instruction: string;
  hint: string;
}

interface Problem {
  question: string;
  steps: ProblemStep[];
  solution: string;
  answer: number;
}

interface QuizQuestion {
  id: number;
  text: string;
  options: string[];
  correct: string;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

function generateProblemFromTopic(topic: Topic, subtopic?: string): Problem {
  const targetSubtopic = subtopic && topic.subtopics?.find(st => st.subtopic === subtopic);

  return {
    question: `Solve the problem related to ${topic.topic}${subtopic ? ` - ${subtopic}` : ''}`,
    steps: [
      { step: 1, instruction: `Understand the concept of ${topic.topic}`, hint: topic.description || "Review the basic concepts" },
      { step: 2, instruction: "Apply the relevant formula or principle", hint: "Use the correct formula" },
      { step: 3, instruction: "Calculate and verify your answer", hint: "Double-check your calculations" },
    ],
    solution: `Solution for ${topic.topic} will be shown here.`,
    answer: 42,
  };
}

function generateQuizFromTopics(topics: Topic[], limit: number = 5): QuizQuestion[] {
  if (!topics || topics.length === 0) return [];

  const questions: QuizQuestion[] = [];
  const topicsToUse = topics.slice(0, limit);

  topicsToUse.forEach((topic, idx) => {
    if (topic.subtopics && topic.subtopics.length > 0) {
      const subtopic = topic.subtopics[0];
      questions.push({
        id: idx + 1,
        text: `What is ${subtopic.subtopic} in the context of ${topic.topic}?`,
        options: [
          `Definition of ${subtopic.subtopic}`,
          `Application of ${subtopic.subtopic}`,
          `Examples of ${subtopic.subtopic}`,
          `Importance of ${subtopic.subtopic}`
        ],
        correct: `Definition of ${subtopic.subtopic}`,
      });
    } else {
      questions.push({
        id: idx + 1,
        text: `What is the main concept of ${topic.topic}?`,
        options: [
          `Definition of ${topic.topic}`,
          `Applications of ${topic.topic}`,
          `Examples of ${topic.topic}`,
          `Importance of ${topic.topic}`
        ],
        correct: `Definition of ${topic.topic}`,
      });
    }
  });

  return questions;
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface InteractiveClassroomProps {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
}

export default function InteractiveClassroom({
  initialBoard,
  initialClass,
  initialSubject,
}: InteractiveClassroomProps) {
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();

  // State for API data
  const [knowledgeMaps, setKnowledgeMaps] = useState<KnowledgeMap[]>([]);
  const [loading, setLoading] = useState(false);

  // Selection state
  // const [selectedBoard, setSelectedBoard] = useState<string>(initialBoard ?? '');
  // const [selectedClass, setSelectedClass] = useState<string>(initialClass ?? '');
  // const [selectedSubject, setSelectedSubject] = useState<string>(initialSubject ?? '');
  const { board: selectedBoard, classVal: selectedClass, subject: selectedSubject } = useRecoilValue(curriculumSelectionAtom);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [selectedSubtopic, setSelectedSubtopic] = useState<string>('');

  // Derived data
  // const [availableBoards, setAvailableBoards] = useState<string[]>([]);
  // const [availableClasses, setAvailableClasses] = useState<string[]>([]);
  // const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);
  const [availableTopics, setAvailableTopics] = useState<{ label: string; value: string }[]>([]);
  const [availableSubtopics, setAvailableSubtopics] = useState<{ label: string; value: string }[]>([]);
  const [currentKnowledgeMap, setCurrentKnowledgeMap] = useState<KnowledgeMap | null>(null);
  const [currentTopicData, setCurrentTopicData] = useState<Topic | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState('classDemo');
  const [problemAnswer, setProblemAnswer] = useState('');
  const [problemFeedback, setProblemFeedback] = useState<{ show: boolean; correct: boolean; message: string } | null>(null);
  const [quizAnswers, setQuizAnswers] = useState<Record<number, string>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState<{ correct: number; total: number; percentage: number } | null>(null);
  const [openQuizDialog, setOpenQuizDialog] = useState(false);
  const [currentQuizType, setCurrentQuizType] = useState<'weekly' | 'surprise' | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [deliverables, setDeliverables] = useState<StudentDeliverable[]>([]);
  const [evaluating, setEvaluating] = useState(false);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Interactive Classroom' },
    ...(selectedBoard ? [{ label: selectedBoard }] : []),
    ...(selectedClass ? [{ label: `Class ${selectedClass}` }] : []),
    ...(selectedSubject ? [{ label: selectedSubject }] : []),
  ];

  // Fetch all knowledge maps on component mount
  useEffect(() => {
    fetchKnowledgeMaps();
  }, []);

  // useEffect(() => {
  //   if (initialBoard) setSelectedBoard(initialBoard);
  //   if (initialClass) setSelectedClass(initialClass);
  //   if (initialSubject) setSelectedSubject(initialSubject);
  // }, [initialBoard, initialClass, initialSubject]);

  const fetchKnowledgeMaps = async () => {
    try {
      setLoading(true);
      const maps = await aiActions.getKnowledgeList();
      setKnowledgeMaps(maps || []);

      // const boards = [...new Set(maps.map((map: KnowledgeMap) => map.board).filter(Boolean))].sort();
      // setAvailableBoards(boards);
    } catch (error) {
      console.error('Error fetching knowledge maps:', error);
      showSnackBar('Failed to load knowledge maps', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update available classes when board changes
  // useEffect(() => {
  //   if (selectedBoard) {
  //     const classes = [...new Set(
  //       knowledgeMaps.filter(km => km.board === selectedBoard).map(km => km.class)
  //     )].sort();
  //     setAvailableClasses(classes);
  //   } else {
  //     setAvailableClasses([]);
  //   }
  // }, [selectedBoard, knowledgeMaps]);

  // Update available subjects when class changes
  // useEffect(() => {
  //   if (selectedBoard && selectedClass) {
  //     const subjects = [...new Set(
  //       knowledgeMaps.filter(km => km.board === selectedBoard && km.class === selectedClass).map(km => km.subject)
  //     )].sort();
  //     setAvailableSubjects(subjects);
  //   } else {
  //     setAvailableSubjects([]);
  //   }
  // }, [selectedBoard, selectedClass, knowledgeMaps]);

  // Update current knowledge map when selection changes
  useEffect(() => {
    if (selectedBoard && selectedClass && selectedSubject) {
      const found = knowledgeMaps.find(
        (km) => km.board === selectedBoard && km.class === selectedClass && km.subject === selectedSubject
      );
      setCurrentKnowledgeMap(found || null);

      if (found && found.topics) {
        setAvailableTopics(found.topics.map(t => ({ label: t.topic, value: t.topic })));
      } else {
        setAvailableTopics([]);
      }
      setSelectedTopic('');
      setSelectedSubtopic('');
      setCurrentTopicData(null);
      setDeliverables([]);

      if (found) {
        const kmId = found.id ?? found._id ?? '';
        resolveCurriculumChain(kmId, aiActions)
          .then((chain) => setDeliverables(chain.deliverables ?? []))
          .catch(() => setDeliverables([]));
      }
    } else {
      setCurrentKnowledgeMap(null);
      setAvailableTopics([]);
    }
  }, [selectedBoard, selectedClass, selectedSubject, knowledgeMaps]);

  // Update subtopics when topic changes
  useEffect(() => {
    if (currentKnowledgeMap && selectedTopic) {
      const topic = currentKnowledgeMap.topics.find(t => t.topic === selectedTopic);
      setCurrentTopicData(topic || null);

      if (topic && topic.subtopics) {
        setAvailableSubtopics(topic.subtopics.map(st => ({ label: st.subtopic, value: st.subtopic })));
      } else {
        setAvailableSubtopics([]);
      }
      setSelectedSubtopic('');
      setCurrentStep(0);
    }
  }, [selectedTopic, currentKnowledgeMap]);

  const currentDeliverable = deliverables.find(
    (d) =>
      d.topic === selectedTopic ||
      (selectedSubtopic && d.topic?.includes(selectedSubtopic))
  );

  const mockProblem = currentTopicData ? generateProblemFromTopic(currentTopicData, selectedSubtopic) : null;
  const currentProblem = currentDeliverable?.class_demo
    ? {
      question: currentDeliverable.class_demo,
      steps: [{ step: 1, instruction: currentDeliverable.classwork ?? 'Work through the demo', hint: 'Review the class demo' }],
      solution: currentDeliverable.homework ?? '',
      answer: 0,
    }
    : mockProblem;

  const weeklyQuizQuestions = currentKnowledgeMap?.topics ? generateQuizFromTopics(currentKnowledgeMap.topics, 5) : [];
  const surpriseQuizQuestions = currentTopicData ? generateQuizFromTopics([currentTopicData], 3) : [];

  const hasValidSelection = selectedBoard && selectedClass && selectedSubject && selectedTopic;
  const hasProblem = currentProblem?.question;

  const handleProblemSubmit = async () => {
    if (!currentProblem || !problemAnswer.trim()) return;

    if (currentDeliverable?.class_demo) {
      setEvaluating(true);
      try {
        const result = await aiActions.evaluateAnswer({
          question_text: currentDeliverable.class_demo,
          student_answer: problemAnswer,
          reference_answer: currentDeliverable.classwork ?? currentDeliverable.homework ?? '',
          topic: selectedTopic,
        });
        setProblemFeedback({
          show: true,
          correct: result.score >= (result.max_marks ?? 5) * 0.6,
          message: `${result.feedback} (Score: ${result.score}/${result.max_marks ?? 5})`,
        });
      } catch {
        setProblemFeedback({ show: true, correct: false, message: 'Evaluation failed. Please try again.' });
      } finally {
        setEvaluating(false);
        setProblemAnswer('');
        setTimeout(() => setProblemFeedback(null), 5000);
      }
      return;
    }

    const isCorrect = parseInt(problemAnswer) === currentProblem.answer;
    setProblemFeedback({
      show: true,
      correct: isCorrect,
      message: isCorrect ? '✅ Correct! Great work!' : `❌ Incorrect. ${currentProblem.solution}`,
    });
    setTimeout(() => setProblemFeedback(null), 3000);
    setProblemAnswer('');
  };

  const handleQuizAnswer = (questionId: number, answer: string) => {
    setQuizAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleQuizSubmit = () => {
    const questions = currentQuizType === 'weekly' ? weeklyQuizQuestions : surpriseQuizQuestions;
    let correct = 0;
    questions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) correct++;
    });
    setQuizScore({ correct, total: questions.length, percentage: (correct / questions.length) * 100 });
    setQuizSubmitted(true);

    showSnackBar(`Quiz completed! Score: ${correct}/${questions.length}`,
      correct >= questions.length / 2 ? 'success' : 'warning'
    );
  };

  const openQuiz = (type: 'weekly' | 'surprise') => {
    const questions = type === 'weekly' ? weeklyQuizQuestions : surpriseQuizQuestions;
    if (!questions || questions.length === 0) {
      showSnackBar('No quiz questions available for this topic', 'warning');
      return;
    }
    setCurrentQuizType(type);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
    setOpenQuizDialog(true);
  };

  const handleNextStep = () => {
    if (currentProblem && currentStep < currentProblem.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const currentQuizQuestions = currentQuizType === 'weekly' ? weeklyQuizQuestions : surpriseQuizQuestions;

  // // Dropdown options
  // const boardOptions = availableBoards.map(board => ({ label: board, value: board }));
  // const classOptions = availableClasses.map(cls => ({ label: `Class ${cls}`, value: cls }));
  // const subjectOptions = availableSubjects.map(sub => ({ label: sub, value: sub }));
  const topicOptions = availableTopics;

  return (
    <Box sx={{ p: 1.5, bgcolor: '#F5F7FA', minHeight: '100vh', paddingLeft:{md:'200px',sm:'100%'} }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <DynamicBreadcrumbs items={breadcrumbItems} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={fetchKnowledgeMaps}
          disabled={loading}
          sx={{ fontSize: '10px', minHeight: 28, py: 0 }}
        >
          Refresh
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Selection Panel */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#FFFFFF' }}>
        <Grid container spacing={1}>
          {/* <Grid item xs={12} sm={3}>
            <SearchableDropdown
              options={boardOptions}
              labelKey="label"
              valueKey="value"
              label="Board"
              value={selectedBoard ? { label: selectedBoard, value: selectedBoard } : null}
              onChange={(val: any) => { 
                setSelectedBoard(val?.value || ''); 
                setSelectedClass(''); 
                setSelectedSubject('');
                setSelectedTopic('');
              }}
              loading={loading}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
            />
          </Grid> */}
          {/* <Grid item xs={12} sm={3}>
            <SearchableDropdown
              options={classOptions}
              labelKey="label"
              valueKey="value"
              label="Class"
              value={selectedClass ? { label: `Class ${selectedClass}`, value: selectedClass } : null}
              onChange={(val: any) => { 
                setSelectedClass(val?.value || ''); 
                setSelectedSubject('');
                setSelectedTopic('');
              }}
              loading={loading}
              disabled={!selectedBoard}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
            />
          </Grid> */}
          {/* <Grid item xs={12} sm={3}>
            <SearchableDropdown
              options={subjectOptions}
              labelKey="label"
              valueKey="value"
              label="Subject"
              value={selectedSubject ? { label: selectedSubject, value: selectedSubject } : null}
              onChange={(val: any) => { 
                setSelectedSubject(val?.value || ''); 
                setSelectedTopic('');
              }}
              loading={loading}
              disabled={!selectedClass}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
            />
          </Grid> */}
          <Grid item xs={12}>
            <BoardClassSubjectSelector
              externalLoading={loading}
              colProps={{ xs: 12, sm: 3 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SearchableDropdown
              options={topicOptions}
              labelKey="label"
              valueKey="value"
              label="Topic"
              value={selectedTopic ? { label: selectedTopic, value: selectedTopic } : null}
              onChange={(val: any) => setSelectedTopic(val?.value || '')}
              loading={loading}
              disabled={!selectedSubject}
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
            />
          </Grid>
          {availableSubtopics.length > 0 && (
            <Grid item xs={12} sm={6}>
              <SearchableDropdown
                options={availableSubtopics}
                labelKey="label"
                valueKey="value"
                label="Subtopic (Optional)"
                value={selectedSubtopic ? { label: selectedSubtopic, value: selectedSubtopic } : null}
                onChange={(val: any) => setSelectedSubtopic(val?.value || '')}
                size="small"
                sx={{ '& .MuiInputBase-input': { fontSize: '10px', py: 0.75 } }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
      {/* No Selection Message */}
      {!loading && (!hasValidSelection) && (
        <Alert severity="info" sx={{ fontSize: '10px', py: 0, mb: 1.5, '& .MuiAlert-message': { fontSize: '10px' } }}>
          Please select Board, Class, Subject, and Topic to start the interactive session
        </Alert>
      )}

      {/* Topic Info Card */}
      {hasValidSelection && currentTopicData && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: '#90CAF9', borderRadius: 2, bgcolor: '#E3F2FD' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={8}>
              <Typography variant="caption" color="#1565C0" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Current Topic</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0D47A1', mt: 0.5 }}>{selectedTopic}</Typography>
              {selectedSubtopic && (
                <Chip label={selectedSubtopic} size="small" variant="outlined" sx={{ mt: 1, height: 24, fontSize: 11, borderColor: '#64B5F6', bgcolor: '#BBDEFB', color: '#0D47A1', fontWeight: 600 }} />
              )}
              {currentTopicData.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                  {currentTopicData.description}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'right' }}>
              <Chip
                label={`${currentTopicData.subtopics?.length || 0} Subtopics`}
                size="medium"
                variant="filled"
                sx={{ fontWeight: 600, bgcolor: '#1976d2', color: 'white' }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {hasValidSelection && hasProblem && (
        <>
          {/* Navigation Tabs */}
          <Paper elevation={0} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Box sx={{ display: 'flex', p: 0.5, gap: 0.5 }}>
              {[
                { id: 'classDemo', label: 'Class Demo', icon: <PlayIcon sx={{ fontSize: 14 }} /> },
                { id: 'solveTogether', label: 'Solve Together', icon: <GroupIcon sx={{ fontSize: 14 }} /> },
                { id: 'tutorial', label: 'Tutorial', icon: <PlayIcon sx={{ fontSize: 14 }} /> },
              ].map(tab => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'contained' : 'outlined'}
                  onClick={() => setActiveTab(tab.id)}
                  startIcon={tab.icon}
                  size="small"
                  sx={{ flex: 1, fontSize: '10px', minHeight: 32 }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Paper>

          {/* Main Content Area */}
          <Grid container spacing={3}>
            {/* Left - Problem/Content Area */}
            <Grid item xs={12} md={8}>
              <Card elevation={0} variant="outlined" sx={{ borderRadius: 2, minHeight: 400 }}>
                <CardContent sx={{ p: 3 }}>
                  {activeTab === 'classDemo' && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1976d2' }}>📝 Today's Problem</Typography>
                      {currentDeliverable && (
                        <Box sx={{ mb: 2 }}>
                          {currentDeliverable.student_notes && (
                            <Alert severity="info" sx={{ mb: 1, fontSize: 11 }}>{currentDeliverable.student_notes}</Alert>
                          )}
                          {currentDeliverable.classwork && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              Classwork: {currentDeliverable.classwork}
                            </Typography>
                          )}
                          {currentDeliverable.homework && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              Homework: {currentDeliverable.homework}
                            </Typography>
                          )}
                        </Box>
                      )}
                      <Paper elevation={0} variant="outlined" sx={{ p: 2, bgcolor: '#E3F2FD', mb: 3, borderRadius: 2, borderColor: '#90CAF9' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {currentProblem.question}
                        </Typography>
                      </Paper>

                      <Typography variant="subtitle2" sx={{ mb: 1, display: 'block', fontWeight: 700 }}>Solution Steps:</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stepper orientation="vertical" activeStep={currentStep}>
                        {currentProblem.steps.map((step, idx) => (
                          <Step key={idx} completed={currentStep > idx} active={currentStep === idx}>
                            <StepLabel>
                              <Typography variant="body2" sx={{ fontWeight: currentStep === idx ? 700 : 400 }}>{step.instruction}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>💡 {step.hint}</Typography>
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>

                      {currentStep < currentProblem.steps.length - 1 && (
                        <Button variant="outlined" size="medium" onClick={handleNextStep} sx={{ mt: 2, fontWeight: 600 }}>
                          Next Step
                        </Button>
                      )}

                      <Box sx={{ mt: 4, pt: 3, borderTop: '1px dashed #E0E0E0' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Final Answer:</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            fullWidth
                            label="Your Answer"
                            type={currentDeliverable ? 'text' : 'number'}
                            value={problemAnswer}
                            onChange={(e) => setProblemAnswer(e.target.value)}
                            size="medium"
                            multiline={!!currentDeliverable}
                            rows={currentDeliverable ? 2 : 1}
                          />
                          <Button
                            variant="contained"
                            size="medium"
                            onClick={handleProblemSubmit}
                            startIcon={<SendIcon />}
                            disabled={!problemAnswer || evaluating}
                            sx={{ minWidth: 160, fontWeight: 700 }}
                          >
                            {evaluating ? 'Evaluating...' : 'Submit'}
                          </Button>
                        </Box>
                      </Box>
                    </>
                  )}

                  {activeTab === 'solveTogether' && (
                    <>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: '#2E7D32' }}>👥 Teacher & Student Solve Together</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Work through the problem step by step with your teacher
                      </Typography>

                      <Paper elevation={0} variant="outlined" sx={{ p: 2, bgcolor: '#FFF8E1', mb: 3, borderRadius: 2, borderColor: '#FFE0B2' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{currentProblem.question}</Typography>
                      </Paper>

                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4, p: 2, bgcolor: '#F5F5F5', borderRadius: 2 }}>
                        <Avatar sx={{ width: 40, height: 40, bgcolor: '#4caf50' }}>👩‍🏫</Avatar>
                        <Box flex={1}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Teacher's Progress Tracker</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={((currentStep + 1) / currentProblem.steps.length) * 100}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
                            Step {currentStep + 1} of {currentProblem.steps.length}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                          {currentProblem.steps[currentStep]?.instruction}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, bgcolor: '#E8F5E9', borderRadius: 1, borderLeft: '4px solid #4CAF50' }}>
                          💡 Hint: {currentProblem.steps[currentStep]?.hint}
                        </Typography>
                      </Box>

                      {currentStep < currentProblem.steps.length - 1 && (
                        <Button variant="contained" size="large" onClick={handleNextStep} sx={{ mt: 4, fontWeight: 700, borderRadius: 2 }}>
                          Advance to Next Step
                        </Button>
                      )}
                    </>
                  )}

                  {activeTab === 'tutorial' && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#F57F17' }}>📹 Video Tutorial</Typography>
                      <Paper elevation={0} variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: '#FAFAFA', borderRadius: 2, borderStyle: 'dashed' }}>
                        <PlayIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Click to play interactive video tutorial</Typography>
                      </Paper>
                      {currentTopicData?.learning_material && currentTopicData.learning_material.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Reference Learning Resources:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {currentTopicData.learning_material.slice(0, 4).map((material, idx) => (
                              <Chip key={idx} label={material} size="medium" variant="outlined" sx={{ bgcolor: '#FFFDE7' }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right - Quizzes and Assessments */}
            <Grid item xs={12} md={4}>
              <Card elevation={0} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700 }}>🎯 Quick Assessments</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Button
                    variant="outlined"
                    fullWidth
                    size="medium"
                    startIcon={<QuizIcon />}
                    onClick={() => openQuiz('surprise')}
                    sx={{ mb: 1.5, fontWeight: 600, color: '#E65100', borderColor: '#FFCC80', borderRadius: 1.5 }}
                    disabled={surpriseQuizQuestions.length === 0}
                  >
                    Surprise Quiz
                  </Button>

                  <Button
                    variant="contained"
                    fullWidth
                    size="medium"
                    startIcon={<QuizIcon />}
                    onClick={() => openQuiz('weekly')}
                    sx={{ fontWeight: 600, bgcolor: '#1976d2', borderRadius: 1.5 }}
                    disabled={weeklyQuizQuestions.length === 0}
                  >
                    Weekly Quiz (Class Level)
                  </Button>
                </CardContent>
              </Card>

              <Card elevation={0} variant="outlined" sx={{ mb: 2, bgcolor: '#E3F2FD', borderColor: '#90CAF9', borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700, color: '#1565C0' }}>📚 Topic Details</Typography>
                  <Divider sx={{ mb: 1.5, borderColor: '#BBDEFB' }} />
                  <Typography variant="body2" display="block" color="text.secondary" sx={{ mb: 1 }}>
                    <strong style={{ color: '#0D47A1' }}>Topic:</strong> {selectedTopic}
                  </Typography>
                  {selectedSubtopic && (
                    <Typography variant="body2" display="block" color="text.secondary" sx={{ mb: 1 }}>
                      <strong style={{ color: '#0D47A1' }}>Subtopic:</strong> {selectedSubtopic}
                    </Typography>
                  )}
                  <Typography variant="body2" display="block" color="text.secondary" sx={{ mb: 1 }}>
                    <strong style={{ color: '#0D47A1' }}>Total Subtopics:</strong> {currentTopicData?.subtopics?.length || 0}
                  </Typography>
                  {currentTopicData?.topic_prerequisites && currentTopicData.topic_prerequisites.length > 0 && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: '#BBDEFB', borderRadius: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                        Prerequisites: {currentTopicData.topic_prerequisites.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>🏆 Recent Achievements</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Box textAlign="center" sx={{ p: 1.5, '&:hover': { bgcolor: '#FFF8E1', borderRadius: 2 } }}>
                      <TrophyIcon sx={{ fontSize: 32, color: '#FFCA28', mb: 1 }} />
                      <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>Quiz Master</Typography>
                    </Box>
                    <Box textAlign="center" sx={{ p: 1.5, '&:hover': { bgcolor: '#E8F5E9', borderRadius: 2 } }}>
                      <CheckIcon sx={{ fontSize: 32, color: '#66BB6A', mb: 1 }} />
                      <Typography variant="caption" display="block" sx={{ fontWeight: 700 }}>Perfect Score</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Quiz Dialog */}
      <Dialog open={openQuizDialog} onClose={() => setOpenQuizDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '14px', py: 1.5 }}>
          {currentQuizType === 'weekly' ? 'Weekly Class Quiz' : 'Surprise Quiz!'}
          {quizSubmitted && quizScore && (
            <Chip
              label={`Score: ${quizScore.correct}/${quizScore.total} (${Math.round(quizScore.percentage)}%)`}
              color={quizScore.percentage >= 60 ? 'success' : 'warning'}
              size="small"
              sx={{ ml: 1, height: 24, fontSize: 10 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {!quizSubmitted ? (
            currentQuizQuestions.length > 0 ? (
              currentQuizQuestions.map((q, idx) => (
                <Box key={q.id} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: 11, fontWeight: 500 }}>{idx + 1}. {q.text}</Typography>
                  <RadioGroup
                    value={quizAnswers[q.id] || ''}
                    onChange={(e) => handleQuizAnswer(q.id, e.target.value)}
                    sx={{ ml: 2 }}
                  >
                    {q.options.map(opt => (
                      <FormControlLabel
                        key={opt}
                        value={opt}
                        control={<Radio size="small" />}
                        label={opt}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: 10 } }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3, fontSize: 10 }}>
                No quiz questions available for this topic. Please select a different topic.
              </Typography>
            )
          ) : (
            quizScore && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h4" color={quizScore.percentage >= 60 ? 'success.main' : 'warning.main'} sx={{ fontSize: 32 }}>
                  {Math.round(quizScore.percentage)}%
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 11 }}>
                  You got {quizScore.correct} out of {quizScore.total} correct!
                </Typography>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setOpenQuizDialog(false)} size="small" sx={{ fontSize: '10px' }}>Close</Button>
          {!quizSubmitted && currentQuizQuestions.length > 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleQuizSubmit}
              disabled={Object.keys(quizAnswers).length !== currentQuizQuestions.length}
              sx={{ fontSize: '10px' }}
            >
              Submit Quiz
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Feedback Alert */}
      {problemFeedback?.show && (
        <Alert
          severity={problemFeedback.correct ? 'success' : 'error'}
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000, fontSize: '10px' }}
          onClose={() => setProblemFeedback(null)}
        >
          {problemFeedback.message}
        </Alert>
      )}
    </Box>
  );
}